package com.evoapps.evoshift.domain.usecase.team

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.Announcement
import com.evoapps.evoshift.domain.model.Notification
import com.evoapps.evoshift.domain.model.NotificationData
import com.evoapps.evoshift.domain.model.NotificationType
import com.evoapps.evoshift.domain.model.ShiftNote
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.repository.AnnouncementRepository
import com.evoapps.evoshift.domain.repository.ShiftNoteRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import kotlin.time.Clock

/** Why a post or a note cannot be saved (the screens keep the button off for these). */
class InvalidMessageException(val problem: Problem, val maxLength: Int = 0) : Exception("Invalid message: $problem") {
    enum class Problem { EMPTY, TOO_LONG, NOBODY_ON_SHIFT }
}

/**
 * A manager posts to the team (SH-118): the text (trimmed, up to [Announcement.MAX_TEXT]), pinned
 * or not, to everyone or to one role. The server sends it to the inboxes with a push
 * (functions/announcements.js); firestore.rules let only the workplace's managers post.
 */
class PostAnnouncementUseCase(
    private val repository: AnnouncementRepository,
    private val clock: Clock = AppClock
) {
    suspend operator fun invoke(
        workplaceId: String,
        author: User,
        text: String,
        pinned: Boolean,
        targetRole: String?
    ): Result<Announcement> = runCatching {
        val body = text.trim()
        if (body.isEmpty()) throw InvalidMessageException(InvalidMessageException.Problem.EMPTY)
        if (body.length > Announcement.MAX_TEXT) throw InvalidMessageException(InvalidMessageException.Problem.TOO_LONG, Announcement.MAX_TEXT)
        val announcement = Announcement(
            id = "",
            workplaceId = workplaceId,
            authorId = author.uid,
            authorName = author.displayName.trim().take(100),
            text = body,
            pinned = pinned,
            targetRole = targetRole?.trim()?.takeIf { it.isNotEmpty() }?.take(Announcement.MAX_ROLE),
            createdAt = clock.now().toString()
        )
        val id = repository.post(announcement).getOrThrow()
        announcement.copy(id = id)
    }
}

/**
 * The reader saw [announcements]: a read receipt for each one not read yet ([alreadyRead]), for
 * the managers' "read by" (SH-118). Best effort: a receipt that fails is tried again next time.
 * Returns the ids now recorded.
 */
class MarkAnnouncementsReadUseCase(
    private val repository: AnnouncementRepository,
    private val clock: Clock = AppClock
) {
    suspend operator fun invoke(
        workplaceId: String,
        userId: String,
        announcements: List<Announcement>,
        alreadyRead: Set<String>
    ): Set<String> {
        val now = clock.now().toString()
        return announcements
            .filter { it.id.isNotEmpty() && it.id !in alreadyRead && it.authorId != userId }
            .filter { repository.markRead(workplaceId, it.id, userId, now).isSuccess }
            .mapTo(mutableSetOf()) { it.id }
    }
}

/**
 * A manager's note on a shift for the person working it (SH-118). A blank text removes the note.
 * The note follows the shift's current assignee; on a published shift that person also gets a
 * notification (and push) with it. Unchanged text sends nothing.
 */
class SaveShiftNoteUseCase(
    private val shiftRepository: ShiftRepository,
    private val noteRepository: ShiftNoteRepository,
    private val userRepository: UserRepository,
    private val clock: Clock = AppClock
) {
    suspend operator fun invoke(workplaceId: String, shiftId: String, text: String, editorId: String): Result<ShiftNote?> = runCatching {
        val body = text.trim()
        if (body.length > ShiftNote.MAX_NOTE) throw InvalidMessageException(InvalidMessageException.Problem.TOO_LONG, ShiftNote.MAX_NOTE)
        val before = noteRepository.get(workplaceId, shiftId).getOrNull()
        if (body.isEmpty()) {
            if (before != null) noteRepository.delete(workplaceId, shiftId).getOrThrow()
            return@runCatching null
        }
        val shift = shiftRepository.getShift(shiftId).getOrThrow()
        val assignee = shift.userId ?: throw InvalidMessageException(InvalidMessageException.Problem.NOBODY_ON_SHIFT)
        if (before != null && before.note == body && before.userId == assignee) return@runCatching before
        val now = clock.now().toString()
        val note = ShiftNote(shiftId, workplaceId, assignee, body, now, editorId)
        noteRepository.save(note).getOrThrow()
        if (shift.isPublished) {
            userRepository.sendNotification(
                Notification(
                    userId = assignee,
                    title = "Note on your shift",
                    message = body,
                    type = NotificationType.GENERAL,
                    createdAt = now,
                    data = NotificationData.forShift(
                        shift, workplaceId,
                        NotificationData.KIND to NotificationData.Kind.SHIFT_NOTE,
                        NotificationData.NOTE to body
                    )
                )
            )
        }
        note
    }
}
